import { ComponentType, useMemo } from 'react';
import { useIntl } from 'react-intl';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  IconActivity,
  IconBrain,
  IconDatabaseSearch,
  IconMathFunction,
  IconSTurnRight,
  IconWebhook,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Dialog,
  DialogContent,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  buildPhaseTimeline,
  isPhaseCategory,
  PHASE_CATEGORIES,
  PhaseCategory,
  PhaseNode,
} from './spanTimeline';

const CATEGORY_COLOR: Record<PhaseCategory, string> = {
  formula: '#f0a45d',
  hook: '#6ea8fe',
  reasoning: '#a78bfa',
  lookup: '#26a69a',
  rules: '#ec6a8e',
};

const CATEGORY_ICON: Record<PhaseCategory, ComponentType> = {
  formula: IconMathFunction,
  hook: IconWebhook,
  reasoning: IconBrain,
  lookup: IconDatabaseSearch,
  rules: IconSTurnRight,
};

const UNKNOWN_COLOR = '#8b93a7';

const categoryColor = (category: string) =>
  isPhaseCategory(category) ? CATEGORY_COLOR[category] : UNKNOWN_COLOR;

const categoryIcon = (category: string): ComponentType =>
  isPhaseCategory(category) ? CATEGORY_ICON[category] : IconActivity;

const ROW_HEIGHT = 40;

const formatMs = (ms: number) => `${ms.toFixed(1)}ms`;

const formatAttr = (value: unknown) =>
  Array.isArray(value) ? value.join(', ') : String(value);

const NodeTooltip = ({ node }: { node: PhaseNode }) => (
  <Stack spacing={0.25} sx={{ py: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {node.category}
    </Typography>
    <Typography variant="caption">{formatMs(node.durMs)}</Typography>
    {Object.entries(node.attributes).map(([name, value]) => (
      <Typography key={name} variant="caption">
        {name}: {formatAttr(value)}
      </Typography>
    ))}
  </Stack>
);

// Single-row timeline: every node is positioned by its real time, and deeper
// children are layered over their parents (via z-index), so the innermost
// active phase is what shows at any point.
const PhaseTrack = ({
  nodes,
  wallMs,
}: {
  nodes: PhaseNode[];
  wallMs: number;
}) => {
  const scale = (ms: number) => (wallMs > 0 ? (ms / wallMs) * 100 : 0);

  return (
    <Box sx={{ position: 'relative', height: ROW_HEIGHT, mx: 3, my: 3 }}>
      {nodes.map(node => (
        <Tooltip
          key={`${node.category}-${node.depth}-${node.offsetMs}`}
          title={<NodeTooltip node={node} />}
        >
          <Box
            sx={{
              position: 'absolute',
              top: node.depth * 2,
              left: `calc(${scale(node.offsetMs)}% + ${node.depth * 2}px)`,
              width: `${scale(node.durMs)}%`,
              height: ROW_HEIGHT - node.depth * 4,
              zIndex: node.depth + 1,
              bgcolor: categoryColor(node.category),
              borderRadius: 0,
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

const LegendItem = ({ category }: { category: PhaseCategory }) => {
  const Icon = categoryIcon(category);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: borderRadius('s'),
          bgcolor: categoryColor(category),
        }}
      />
      <SvgIcon sx={{ fontSize: 14, color: 'text.secondary' }}>
        <Icon />
      </SvgIcon>
      <Typography variant="caption" color="text.secondary">
        {category}
      </Typography>
    </Stack>
  );
};

export const ValidationTelemetryModal = ({
  open,
  onClose,
  spans,
}: {
  open: boolean;
  onClose: () => void;
  spans: unknown[] | null;
}) => {
  const intl = useIntl();

  const { nodes, wallMs } = useMemo(
    () => buildPhaseTimeline(spans ?? []),
    [spans]
  );

  const legendCategories = useMemo(
    () =>
      PHASE_CATEGORIES.filter(category =>
        nodes.some(node => node.category === category)
      ),
    [nodes]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' }, elevation: 2 }}
    >
      <DialogTitle
        title={intl.formatMessage({
          id: 'features.validationTelemetry.title',
        })}
        onClose={onClose}
      />
      <Box sx={{ px: 3, pt: 3, pb: 1, textAlign: 'center' }}>
        <Typography variant="h6">{formatMs(wallMs)}</Typography>
      </Box>
      <DialogContent sx={{ p: 0, pb: 2, display: 'flex', overflow: 'hidden' }}>
        {nodes.length === 0 ? (
          <Box sx={{ p: 3, color: 'text.secondary' }}>
            {intl.formatMessage({
              id: 'features.validationTelemetry.noSpans',
            })}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PhaseTrack nodes={nodes} wallMs={wallMs} />
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ px: 3, pb: 2 }}
            >
              {legendCategories.map(category => (
                <LegendItem key={category} category={category} />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
